import {humanEnumeration} from '../../../../components/utils';
import type {ClassData, FeatureData} from './catalog';
import {abilityLabel, armorLabel, weaponLabel} from './labels';
import type {ChooseGroup, FeatureProps} from './types';

type MulticlassProficiencies = {
	armor: string[];
	weapons: string[];
	tools: string[];
	skillCount: number;
	instrumentCount: number;
};

const MULTICLASS_PROFICIENCIES: Record<string, MulticlassProficiencies> = {
	barbarian: {armor: ['Shields'], weapons: ['Martial'], tools: [], skillCount: 0, instrumentCount: 0},
	bard: {armor: ['Light'], weapons: [], tools: [], skillCount: 1, instrumentCount: 1},
	cleric: {armor: ['Light', 'Medium', 'Shields'], weapons: [], tools: [], skillCount: 0, instrumentCount: 0},
	druid: {armor: ['Light', 'Shields'], weapons: [], tools: [], skillCount: 0, instrumentCount: 0},
	fighter: {
		armor: ['Light', 'Medium', 'Shields'],
		weapons: ['Martial'],
		tools: [],
		skillCount: 0,
		instrumentCount: 0,
	},
	monk: {armor: [], weapons: [], tools: [], skillCount: 0, instrumentCount: 0},
	paladin: {
		armor: ['Light', 'Medium', 'Shields'],
		weapons: ['Martial'],
		tools: [],
		skillCount: 0,
		instrumentCount: 0,
	},
	ranger: {armor: ['Light', 'Medium', 'Shields'], weapons: ['Martial'], tools: [], skillCount: 1, instrumentCount: 0},
	rogue: {armor: ['Light'], weapons: [], tools: ['thieves-tools'], skillCount: 1, instrumentCount: 0},
	sorcerer: {armor: [], weapons: [], tools: [], skillCount: 0, instrumentCount: 0},
	warlock: {armor: ['Light'], weapons: [], tools: [], skillCount: 0, instrumentCount: 0},
	wizard: {armor: [], weapons: [], tools: [], skillCount: 0, instrumentCount: 0},
};

export function createClassBaseFeatures(classData: ClassData, isStartingClass: boolean): FeatureData[] {
	return [createHitPointsFeature(classData), createProficienciesFeature(classData, isStartingClass)];
}

function createHitPointsFeature(classData: ClassData): FeatureData {
	const {hitDie, genitiveTitle} = classData;
	const averageRoll = hitDie / 2 + 1;
	return {
		id: `${classData.id}-hit-points`,
		title: 'Здоровье',
		description: [
			`[indent]*Кость здоровья:* D${hitDie} за каждый уровень ${genitiveTitle}.[/indent]`,
			`[indent]*ПЗ на 1-м уровне:* ${hitDie} + модификатор Выносливости.[/indent]`,
			`[indent]*ПЗ на следующих уровнях:* D${hitDie} (или ${averageRoll}) + модификатор Выносливости за каждый уровень ${genitiveTitle} после 1-го.[/indent]`,
		].join('\n'),
		levels: [1],
		order: -2,
		choose: [],
		classId: classData.id,
	};
}

function createProficienciesFeature(classData: ClassData, isStartingClass: boolean): FeatureData {
	const props = isStartingClass ? startingClassProps(classData) : multiclassProps(classData);
	const choose = isStartingClass ? classData.choose : multiclassChoose(classData);
	return {
		id: `${classData.id}-proficiencies`,
		title: 'Умения',
		description: describeProficiencies(props),
		levels: [1],
		order: -1,
		props,
		choose,
		classId: classData.id,
	};
}

const startingClassProps = (classData: ClassData): FeatureProps => ({
	...classData.props,
	savingThrowProficiency: classData.saves,
});

function multiclassProps(classData: ClassData): FeatureProps {
	const proficiencies = MULTICLASS_PROFICIENCIES[classData.id];
	if (!proficiencies) return {};
	return {
		armorProficiencies: proficiencies.armor,
		weaponProficiencies: proficiencies.weapons,
		toolsProficiencies: proficiencies.tools,
	};
}

function multiclassChoose(classData: ClassData): ChooseGroup[] {
	const proficiencies = MULTICLASS_PROFICIENCIES[classData.id];
	if (!proficiencies) return [];
	const startingSkillChoice = classData.choose.find((group) => group.type === 'skill');
	const groups: ChooseGroup[] = [];
	if (proficiencies.skillCount && startingSkillChoice) {
		groups.push({...startingSkillChoice, max: proficiencies.skillCount, multiple: proficiencies.skillCount > 1});
	}
	if (proficiencies.instrumentCount) {
		groups.push({
			key: 'musicalInstrumentProficiency',
			type: 'instrument',
			label: 'Музыкальный инструмент',
			max: proficiencies.instrumentCount,
			multiple: false,
			params: {},
			filters: {},
		});
	}
	return groups;
}

function describeProficiencies(props: FeatureProps): string {
	const lines = [
		['Умения в испытаниях', props.savingThrowProficiency?.map(abilityLabel)],
		['Обращение с бронёй', props.armorProficiencies?.map(armorLabel)],
		['Умения в оружии', props.weaponProficiencies?.map(weaponLabel)],
	] as const;
	return lines
		.filter(([, values]) => values?.length)
		.map(([title, values]) => `[indent]*${title}:* ${humanEnumeration(values as string[])}[/indent]`)
		.join('\n');
}
